import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { colors } from "@/lib/theme";
import { commentTime } from "@/lib/date";
import { Avatar, Spinner } from "@/components/peeps/ui";

export default function Conversation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const endRef = useRef(null);

  const load = useCallback(async (scroll = false) => {
    try {
      const res = await api.conversation(id);
      setData(res);
      if (scroll) setTimeout(() => endRef.current?.scrollIntoView({ behavior: "auto" }), 50);
    } catch (e) {
      setError(e.message || "Couldn't load this conversation");
    }
  }, [id]);

  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), 8000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [data?.messages?.length]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    setText("");
    try {
      const msg = await api.sendMessage(id, t);
      setData((p) => ({ ...p, messages: [...(p?.messages || []), msg] }));
    } catch (e) {
      toast(e.message || "Couldn't send");
      setText(t);
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6" style={{ background: colors.surface }}>
        <p className="font-display text-[15px] mb-4" style={{ color: colors.onSurfaceTertiary }}>{error}</p>
        <button onClick={() => navigate("/messages")} className="font-display font-medium text-[14px] underline" style={{ color: colors.brandSecondary }}>Back to messages</button>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen flex items-center justify-center" style={{ background: colors.surface }}><Spinner /></div>;

  return (
    <div className="flex flex-col h-screen mx-auto w-full max-w-2xl" style={{ background: colors.surface }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 shrink-0" style={{ borderBottom: `1px solid ${colors.border}`, background: colors.surfaceSecondary }}>
        <button data-testid="conversation-back" onClick={() => navigate("/messages")} className="p-1 -ml-1">
          <ChevronLeft size={22} color={colors.onSurface} />
        </button>
        <button data-testid="conversation-user" onClick={() => navigate(`/user/${data.user.id}`)} className="flex items-center gap-2.5 flex-1 min-w-0">
          <Avatar name={data.user.display_name} uri={data.user.profile_photo_url} size={36} />
          <span className="font-display font-bold text-[16px] truncate" style={{ color: colors.onSurface }}>{data.user.display_name}</span>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4" data-testid="conversation-messages">
        {data.messages.length === 0 && (
          <p className="text-center font-display text-[13.5px] mt-8" style={{ color: colors.faint }}>
            Say hi to {data.user.display_name}.
          </p>
        )}
        <div className="flex flex-col gap-2">
          {data.messages.map((m) => (
            <div key={m.id} className={`flex ${m.from_me ? "justify-end" : "justify-start"}`}>
              <div
                data-testid={`message-${m.id}`}
                className="max-w-[78%] rounded-2xl px-3.5 py-2"
                style={{
                  background: m.from_me ? colors.brand : colors.surfaceSecondary,
                  border: m.from_me ? "none" : `1px solid ${colors.border}`,
                  borderBottomRightRadius: m.from_me ? 4 : 16,
                  borderBottomLeftRadius: m.from_me ? 16 : 4,
                }}
              >
                <p className="font-display text-[14px] leading-snug whitespace-pre-wrap break-words" style={{ color: m.from_me ? colors.onBrand : colors.onSurface }}>{m.text}</p>
                <span className="font-mono2 text-[9.5px] block mt-0.5 text-right" style={{ color: m.from_me ? "rgba(255,255,255,0.65)" : colors.faint }}>{commentTime(m.created_at)}</span>
              </div>
            </div>
          ))}
          <div ref={endRef} />
        </div>
      </div>

      {/* Composer */}
      <div className="flex items-end gap-2 px-3 py-3 shrink-0" style={{ borderTop: `1px solid ${colors.border}`, background: colors.surfaceSecondary }}>
        <textarea
          data-testid="message-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          rows={1}
          placeholder={`Message ${data.user.display_name}…`}
          className="flex-1 rounded-2xl px-3.5 py-2.5 font-display text-[14px] resize-none max-h-28"
          style={{ background: colors.surfaceTertiary, border: `1px solid ${colors.border}`, color: colors.onSurface }}
        />
        <button
          data-testid="message-send"
          onClick={send}
          disabled={!text.trim() || sending}
          className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 transition-opacity"
          style={{ background: colors.brand, opacity: !text.trim() || sending ? 0.5 : 1 }}
        >
          <Send size={18} color={colors.onBrand} />
        </button>
      </div>
    </div>
  );
}
